import { gql, GraphQLClient } from 'graphql-request'
import BigNumber from 'bignumber.js'
import { Block } from 'state/info/types'
import { GRAPH_NODE_MIN_BLOCK } from 'config'
import { PoolData } from '../../types'
import { get2DayChange } from '../../utils/data'
import { updatePoolApr } from 'utils/SApiProvider'

export const POOLS_BULK = (block: number | undefined, pools: string[]) => {
  let poolString = `[`
  pools.forEach((address) => {
    poolString = `${poolString}"${address}",`
  })
  poolString += ']'
  const queryString = `
    query pools {
      pools(where: {id_in: ${poolString}},
     ${block ? `block: {number: ${GRAPH_NODE_MIN_BLOCK(block)}} ,` : ``}
     ${pools.length === 1 ? '' : 'orderBy: totalValueLockedUSD, orderDirection: desc'}) {
        id
        feeTier
        liquidity
        sqrtPrice
        tick
        token0 {
            id
            symbol
            name
            decimals
            derivedETH
        }
        token1 {
            id
            symbol
            name
            decimals
            derivedETH
        }
        token0Price
        token1Price
        volumeUSD
        untrackedVolumeUSD
        volumeToken0
        volumeToken1
        txCount
        totalValueLockedToken0
        totalValueLockedToken1
        totalValueLockedUSD
        feesUSD
        protocolFeesUSD
      }
      bundles(where: {id: "1"}) {
        ethPriceUSD
      }
    }
    `
  return gql`
    ${queryString}
  `
}

interface PoolFields {
  id: string
  feeTier: string
  liquidity: string
  sqrtPrice: string
  tick: string
  token0: {
    id: string
    symbol: string
    name: string
    decimals: string
    derivedETH: string
  }
  token1: {
    id: string
    symbol: string
    name: string
    decimals: string
    derivedETH: string
  }
  token0Price: string
  token1Price: string
  volumeUSD: string
  untrackedVolumeUSD: string
  volumeToken0: string
  volumeToken1: string
  txCount: string
  totalValueLockedToken0: string
  totalValueLockedToken1: string
  totalValueLockedUSD: string
  feesUSD: string
  protocolFeesUSD: string
}

interface PoolDataResponse {
  pools: PoolFields[]
  bundles: {
    ethPriceUSD: string
  }[]
}

/**
 * Fetch top addresses by volume
 */
export async function fetchPoolDatas(
  dataClient: GraphQLClient,
  poolAddresses: string[],
  blocks: Block[],
): Promise<{
  error: boolean
  data:
    | {
        [address: string]: PoolData
      }
    | undefined
}> {
  // get blocks from historic timestamps

  try {
    const [block24, block48, blockWeek] = blocks ?? []

    const [data, data24, data48, dataWeek] = await Promise.all([
      dataClient.request<PoolDataResponse>(POOLS_BULK(undefined, poolAddresses)),
      dataClient.request<PoolDataResponse>(POOLS_BULK(block24?.number, poolAddresses)),
      dataClient.request<PoolDataResponse>(POOLS_BULK(block48?.number, poolAddresses)),
      dataClient.request<PoolDataResponse>(POOLS_BULK(block48?.number, poolAddresses)),
    ])

    // return early if not all data yet

    const ethPriceUSD = data?.bundles?.[0]?.ethPriceUSD ? parseFloat(data?.bundles?.[0]?.ethPriceUSD) : 0

    const parsed = data?.pools
      ? data.pools.reduce((accum: { [address: string]: PoolFields }, poolData) => {
          // eslint-disable-next-line no-param-reassign
          accum[poolData.id] = poolData
          return accum
        }, {})
      : {}
    const parsed24 = data24?.pools
      ? data24.pools.reduce((accum: { [address: string]: PoolFields }, poolData) => {
          // eslint-disable-next-line no-param-reassign
          accum[poolData.id] = poolData
          return accum
        }, {})
      : {}
    const parsed48 = data48?.pools
      ? data48.pools.reduce((accum: { [address: string]: PoolFields }, poolData) => {
          // eslint-disable-next-line no-param-reassign
          accum[poolData.id] = poolData
          return accum
        }, {})
      : {}
    const parsedWeek = dataWeek?.pools
      ? dataWeek.pools.reduce((accum: { [address: string]: PoolFields }, poolData) => {
          // eslint-disable-next-line no-param-reassign
          accum[poolData.id] = poolData
          return accum
        }, {})
      : {}

    // format data and calculate daily changes
    const formatted = poolAddresses.reduce((accum: { [address: string]: PoolData }, address) => {
      const current: PoolFields | undefined = parsed[address]
      const oneDay: PoolFields | undefined = parsed24[address]
      const twoDay: PoolFields | undefined = parsed48[address]
      const week: PoolFields | undefined = parsedWeek[address]

      const getVolumeUSD = (d): number => {
        return parseFloat(d.volumeUSD) === 0 ? parseFloat(d.untrackedVolumeUSD) : parseFloat(d.volumeUSD)
      }

      const [volumeUSD, volumeUSDChange] =
        current && oneDay && twoDay
          ? get2DayChange(getVolumeUSD(current), getVolumeUSD(oneDay), getVolumeUSD(twoDay))
          : current
          ? [new BigNumber(getVolumeUSD(current)).toFixed(), '0']
          : ['0', '0']

      const volumeUSDWeek =
        current && week ? getVolumeUSD(current) - getVolumeUSD(week) : current ? getVolumeUSD(current) : 0
      const feeUSD =
        current && oneDay
          ? new BigNumber(current?.feesUSD)
              .minus(current?.protocolFeesUSD)
              .minus(new BigNumber(oneDay?.feesUSD).minus(oneDay?.protocolFeesUSD))
          : new BigNumber(current?.feesUSD).minus(current?.protocolFeesUSD)
      // Hotifx: Subtract fees from TVL to correct data while subgraph is fixed.
      /**
       * Note: see issue desribed here https://github.com/Uniswap/v3-subgraph/issues/74
       * During subgraph deploy switch this month we lost logic to fix this accounting.
       * Grafted sync pending fix now.
       * @chef-jojo: should be fixed on our version, but leaving this in for now
       */
      const feePercent = current ? new BigNumber(current.feeTier).div(10000).div(100).toFixed() : 0
      const tvlAdjust0 = current?.volumeToken0
        ? new BigNumber(current.volumeToken0).multipliedBy(feePercent).div(2).toFixed()
        : '0'
      const tvlAdjust1 = current?.volumeToken1
        ? new BigNumber(current.volumeToken1).multipliedBy(feePercent).div(2).toFixed()
        : '0'
      const tvlToken0 = current
        ? BigNumber.max(new BigNumber(current.totalValueLockedToken0).minus(tvlAdjust0), '0').toFixed()
        : '0'
      const tvlToken1 = current
        ? BigNumber.max(new BigNumber(current.totalValueLockedToken1).minus(tvlAdjust1), '0').toFixed()
        : '0'
      let tvlUSD = current ? new BigNumber(current.totalValueLockedUSD).toFixed() : '0'
      const tvlUSDChange =
        current && oneDay
          ? ((parseFloat(current.totalValueLockedUSD) - parseFloat(oneDay.totalValueLockedUSD)) /
              parseFloat(oneDay.totalValueLockedUSD === '0' ? '1' : oneDay.totalValueLockedUSD)) *
            100
          : 0

      // Part of TVL fix
      const tvlUpdated = current
        ? new BigNumber(tvlToken0)
            .multipliedBy(current.token0.derivedETH)
            .multipliedBy(ethPriceUSD)
            .plus(new BigNumber(tvlToken1).multipliedBy(current.token1.derivedETH).multipliedBy(ethPriceUSD))
            .toFixed()
        : undefined
      if (tvlUpdated) {
        tvlUSD = tvlUpdated
      }

      const feeTier = current ? parseInt(current.feeTier) : 0

      if (current) {
        // eslint-disable-next-line no-param-reassign
        let poolData = {
          address,
          feeTier,
          liquidity: parseFloat(current.liquidity),
          sqrtPrice: parseFloat(current.sqrtPrice),
          tick: parseFloat(current.tick),
          token0: {
            address: current.token0.id,
            name: current.token0.name,
            symbol: current.token0.symbol,
            decimals: parseInt(current.token0.decimals),
            derivedETH: parseFloat(current.token0.derivedETH),
          },
          token1: {
            address: current.token1.id,
            name: current.token1.name,
            symbol: current.token1.symbol,
            decimals: parseInt(current.token1.decimals),
            derivedETH: parseFloat(current.token1.derivedETH),
          },
          token0Price: parseFloat(current.token0Price),
          token1Price: parseFloat(current.token1Price),
          volumeUSD: new BigNumber(volumeUSD).toNumber(),
          volumeUSDChange: new BigNumber(volumeUSDChange).toNumber(),
          volumeUSDWeek: new BigNumber(volumeUSDWeek).toNumber(),
          tvlUSD: new BigNumber(tvlUSD).toNumber(),
          tvlUSDChange,
          tvlToken0: new BigNumber(tvlToken0).toNumber(),
          tvlToken1: new BigNumber(tvlToken1).toNumber(),
          feeUSD: feeUSD.toNumber(),
          apr: 0
        }
        updatePoolApr(poolData)
        accum[address] = poolData
      }

      return accum
    }, {})

    return {
      error: false,
      data: formatted,
    }
  } catch (e) {
    console.error(e)
    return { error: true, data: undefined }
  }
}
